<script lang="ts" setup>
  import type { ProductVariantResource, ProductFlexibleResource } from '@storefront/resources';
  import { computed } from 'vue';
  import { Unit, CustomerType } from '@othercommerce/domain';
  import { Features } from '@shared/features';
  import { useAuthenticated, usePersistentFormContext, useTranslator } from '@othercommerce/foundation';
  import { useCustomerOrderAbility } from '@storefront/storefront';

  interface SelectedItem {
    productId: number,
    variantId: number,
    stockAvailable: number,
    stockOnOrder: boolean,
    isDropShipping: boolean,
    quantity: number,
  }

  interface SelectedExtra {
    extraId: number,
    productId: number,
    variantId: number,
    stockAvailable: number,
    stockOnOrder: boolean,
    isDropShipping: boolean,
    quantity: number,
  }

  interface Props {
    product: ProductFlexibleResource,
    variant: ProductVariantResource,
    required: number,
    processing: boolean,
    submit: () => void,
  }

  const props = defineProps<Props>();
  const user = useAuthenticated();
  const orderable = useCustomerOrderAbility();
  const translator = useTranslator();

  const { data } = usePersistentFormContext();

  const available = computed(() => {
    return (
      data.value.items.every((item: SelectedItem) => item.isDropShipping || item.stockOnOrder || (item.quantity <= item.stockAvailable)) &&
      data.value.extras.every((item: SelectedExtra) => item.isDropShipping || item.stockOnOrder || (item.quantity <= item.stockAvailable))
    );
  });

  const areItemsUnique = computed(() => {
    const seen = new Set();

    return !data.value.items.some((item: SelectedItem) => {
      const key = `${item.productId}-${item.variantId}`;

      if (seen.has(key)) {
        return true;
      }

      seen.add(key);
      return false;
    });
  });

  const status = computed(() => {
    if (props.required > 0) {
      return translator.trans('SHOP.PRODUCT.MESSAGE.PROMOTION_CONDITIONS_NOT_MET');
    }

    if (!orderable.value) {
      return translator.trans('SHOP.PRODUCT.MESSAGE.AUTHENTICATE_TO_BUY_SET');
    }

    if (!available.value) {
      return translator.trans('SHOP.PRODUCT.MESSAGE.SET_SOME_PRODUCTS_NOT_AVAILABLE');
    }

    if (!areItemsUnique.value) {
      return translator.trans('SHOP.PRODUCT.MESSAGE.SET_DUPLICATE_VALIDATION');
    }

    if (props.variant.isMedicinalProduct && Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled) {
      if (!user.value || user.value.type !== CustomerType.MEDICAL) {
        return translator.trans('SHOP.PRODUCT.MESSAGE.MEDICINAL_SET_NOTICE');
      }
    }

    return true;
  });

  const hasMedicinalProducts = computed(() => {
    if (data.value.items.length > 0 && Features.medicalContractorsRegistry.isDisabled && Features.medicines.isEnabled) {
      const isMedicinalInItems = !!data.value.items.find((item: any) => item.isMedicinalProduct);
      const isMedicinalInExtras = !!data.value.extras.find((item: any) => item.isMedicinalProduct);

      return isMedicinalInItems || isMedicinalInExtras;
    }
    return false;
  });

  const price = computed(() => {
    if (data.value.items.length === 0) {
      return {
        stockUnit: Unit.Set,
        stockUnitBase: 1.0000,
        priceNet: props.variant.priceNet,
        priceGross: props.variant.priceGross,
        regularNet: props.variant.priceNet,
        regularGross: props.variant.priceGross,
      };
    }

    let pricing = data.value.items.reduce((accumulator: { net: number, gross: number }, item: SelectedItem) => {
      let flexibleItem = props.product.flexibleItems.find((flexible) => flexible.productId === item.productId)!;

      return {
        net: accumulator.net + ((item.quantity / flexibleItem.stockUnitBase) * flexibleItem.priceNet),
        gross: accumulator.gross + ((item.quantity / flexibleItem.stockUnitBase) * flexibleItem.priceGross),
      };
    }, {
      net: 0,
      gross: 0,
    });

    if (props.required <= 0) {
      pricing = data.value.extras.reduce((accumulator: { net: number, gross: number }, item: SelectedExtra) => {
        let extra = props.product.flexibleExtras.find((flexible) => flexible.id === item.extraId)!;
        let product = extra.products.find((product) => product.productId === item.productId)!;

        return {
          net: accumulator.net + ((item.quantity / product.stockUnitBase) * product.priceNet),
          gross: accumulator.gross + ((item.quantity / product.stockUnitBase) * product.priceGross),
        };
      }, pricing);
    }

    let { net, gross } = pricing;

    return {
      stockUnit: Unit.Set,
      stockUnitBase: 1.0000,
      priceNet: net,
      priceGross: gross,
      regularNet: net,
      regularGross: gross,
    };
  });
</script>
<template>
  <div class="summary-wrapper">
    <div class="summary">
      <h2 class="heading">
        {{ $t('SHOP.PRODUCT.HEADER.PRICE_PER_SET') }}
      </h2>
      <div class="actions">
        <template v-if="hasMedicinalProducts">
          <div class="actions__status">
            {{ $t('SHOP.PRODUCT.MESSAGE.SET_CONTAINS_MEDICINE') }}
          </div>
        </template>
        <template v-else>
          <SfProductPrice :price="price" presentation />
          <SfButton :enabled="status === true" :loading="processing" primary @click="submit">
            <template #default>
              {{ $t('SHOP.PRODUCT.ACTION.ADD_TO_CART') }}
            </template>
            <template #after>
              <SfIconCartAdd aria-hidden="true" />
            </template>
          </SfButton>
        </template>
      </div>
      <div v-if="status !== true" class="summary-status" role="alert" v-html="status" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
  @use '@storefront/styles/mixins/media';

  .summary-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 1rem;
    gap: 1rem;

    .summary-status {
      font-size: 0.75rem;
      font-weight: var(--font-weight-bold);
      color: var(--danger-color);
      text-align: right;
    }

    .summary {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      .heading {
        font-size: 1.125rem;
        font-weight: var(--font-weight-semi);
      }

      .actions {
        display: flex;
        align-items: end;
        gap: 2rem;

        &__status {
          color: var(--sf-status-color-danger);
          background: var(--sf-status-background-danger);
          border: 1px solid var(--sf-status-color-danger);
          border-radius: var(--border-radius);
          text-align: center;
          font-weight: var(--font-weight-bold);
          font-size: 0.75rem;
          padding: 0.75rem;
        }
      }
    }

    @include media.query(lg) {

      .summary {
        width: 100%;

        .actions {
          align-items: stretch;
          gap: 1rem;
        }
      }
    }
  }
</style>
